import { Reveal } from '@progress/kendo-react-animation';
import * as React from 'react';
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  ExpansionPanelContent
} from '@progress/kendo-react-layout';
import _ from 'lodash';
import PuzzleButton from '../UI/button/PuzzleButton';
import PuzzleCheckbox from '../UI/checkbox/PuzzleCheckbox';
import {
  PUZZLE_EXTENDABLE_PANEL_PROPS,
  PUZZLE_EXTENDABLE_PANEL_STATE,
  PUZZLE_EXTENDABLE_PANEL_ITEM
} from './Types';
import Component from '../../dispatcher/modules/component/Component';
import Container from '../layout/container/Container';
import HBox from '../layout/container/h_box/HBox';
import { CheckboxChangeEvent } from '@progress/kendo-react-inputs';

/**
 * @class PuzzleExpandablePanel
 * Defines expandable panels
 */
class PuzzleExpandablePanel extends Component<PUZZLE_EXTENDABLE_PANEL_PROPS, PUZZLE_EXTENDABLE_PANEL_STATE> {
  state: PUZZLE_EXTENDABLE_PANEL_STATE = {
    expanded: {},
    isAllExpanded: true
  };
  /**
   * Returns expanded states for the expandable panels
   * @returns {any} expanded
   */
  getExpandedState = (): object => {
    let { expanded } = this.state;
    let { items = [] } = this.props;

    Object.keys(items).map((group) => {
      if (_.isUndefined(expanded[group])) {
        expanded[group] = true;
      }
    });

    return expanded;
  };

  /**
   * Returns group counted permissions
   * @param {string} group
   */
  getItemsCountPerGroup = (group: string): any => {
    let { items = [], groupHandler, totalItems = [] } = this.props;
    let totalCount = totalItems[group]?.length;
    let totalCountFiltered = items[group]?.length;

    let countFiltered = _.filter(items[group], {
      checked: true
    }).length;

    let count = _.filter(totalItems[group], {
      checked: true
    }).length;

    return ((groupHandler && totalCount) ?
      <>
        <span
          className={'k-text-center'}
          data-cy={`counter-${group}`}
        >{`${count} ${this.translate('of')} ${totalCount} ${this.translate('selected')}`}</span>  
        <PuzzleCheckbox
          name={group}
          data-cy={`group-checkbox${group}`}
          onClick={(e:React.MouseEvent) => e.stopPropagation()}
          onChange={(e:CheckboxChangeEvent) => groupHandler(group, e)}
          checked={count === totalCount || countFiltered === totalCountFiltered}
          className={'!k-pr-0 k-ml-6'}
        /></> 
      : 
      <span
        className={'k-text-center'}
        data-cy={`counter-${group}`}
      >{`${countFiltered} ${this.translate('selected')}`}</span>
    );
  };

  /**
   * Toggles expanded/collapsed states for the expandable panels
   * @param {string} group
   * @param {ExpansionPanelActionEvent | React.MouseEvent} event
   */
  toggleExpandedState = (group: string = '', event: ExpansionPanelActionEvent | React.MouseEvent) => {
    let { isAllExpanded } = this.state;
    let expanded = this.getExpandedState() as object;
    let isGroupExpanded = !_.isEmpty(group);
    let allExpanded = true;

    Object.keys(expanded).map((groupName: string) => {
      if (!isGroupExpanded) {
        expanded[groupName] = !isAllExpanded;
      } else {
        if (group === groupName) {
          expanded[groupName] = !event['expanded'];
        }

        allExpanded = allExpanded && !event['expanded'];
      }
    });

    isGroupExpanded ? (isAllExpanded = allExpanded) : (isAllExpanded = !isAllExpanded);
    this.setState({
      expanded: expanded
    }, () => {
      this.setState({
        isAllExpanded: isAllExpanded
      });
    });
  };

  /**
   * Default item renderer
   * @param item
   */
  defaultItemRenderer = (item: PUZZLE_EXTENDABLE_PANEL_ITEM) => <Container>{item.name}</Container>;

  /**
   * Default group renderer
   * @param group
   */
  defaultGroupRenderer = (group: string) => group;

  /**
   * getter for title component, returns custom renderer if passed, otherwise returns regular title element
   */
  getTitle = () => {
    if (this.props.titleRenderer) {
      return this.props.titleRenderer();
    } else {
      const { title = '' } = this.props;
      return (
        <h4
          className={'tp-expandable-panel-title'}
        >{title}</h4>
      );
    }
  }

  renderBody(): any {
    let { expanded = this.getExpandedState(), isAllExpanded } = this.state;
    let { items = {}, itemRenderer = this.defaultItemRenderer, groupRenderer = this.defaultGroupRenderer, showItemsCountPerGroup = false, className = '' } = this.props;

    if (!expanded || _.isEmpty(expanded)) {
      expanded = this.getExpandedState();
    }

    return (
      <Container
        className={`tp-expandable-panel ${className}`}
      >
        <HBox
          alignItemsCenter
          className={'tp-expandable-panel-header'}
        >
          <PuzzleButton
            aria-label={this.translate('expandToggle')}
            title={this.translate('expandToggle')}
            className={'tp-expandable-toggle-all'}
            iconClass={`tp-icon ${isAllExpanded ? 'tp-collapse-list' : 'tp-expand-list'}`}
            fillMode={'flat'}
            onClick={this.toggleExpandedState.bind(this, undefined)}
            themeColor={'primary'}
          />
          {this.getTitle()}
        </HBox>
        <div className={'tp-expandable-panel-wrapper'}>
          {Object.keys(items).map((group, groupIndex) => (
            <ExpansionPanel
              title={groupRenderer(group)}
              subtitle={showItemsCountPerGroup ? this.getItemsCountPerGroup(group) : undefined}
              key={`expandable-panel-group-${groupIndex}`}
              expanded={expanded[group]}
              onAction={this.toggleExpandedState.bind(this, group)}
            >
              <Reveal>
                {
                  expanded[group] && items[group].map((item: any, index: number) => (
                    <ExpansionPanelContent
                      key={`expandable-panel-group-item-expansion-${index}`}
                    >
                      <div
                        key={`expandable-panel-group-item-${index} `}
                      >
                        {itemRenderer(item, group)}
                      </div>
                    </ExpansionPanelContent>
                  ))
                }
              </Reveal>
            </ExpansionPanel>
          ))}
        </div>
      </Container>
    );
  }
}

export default PuzzleExpandablePanel;
